import React from 'react';
import { useNavigate } from 'react-router-dom';
import { X, Phone, Eye, CalendarPlus, ClipboardList, AlertCircle, Clock } from 'lucide-react';
import { Button } from '../ui/button';
import { toast } from 'sonner';

const BADGE_META = {
  tarea_vencida:       { label: 'Tarea vencida',      color: 'bg-red-100 text-red-700' },
  sin_visita_90d:      { label: 'Sin visita +90d',    color: 'bg-amber-100 text-amber-700' },
  oportunidad_abierta: { label: 'Oportunidad abierta',color: 'bg-emerald-100 text-emerald-700' },
  contrato_vence_12m:  { label: 'Contrato vence <12m',color: 'bg-yellow-100 text-yellow-700' },
};

function Badge({ id }) {
  const m = BADGE_META[id];
  if (!m) return null;
  return (
    <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium ${m.color}`}>
      {m.label}
    </span>
  );
}

export default function MapaEntityPanel({ resumen, onClose }) {
  const navigate = useNavigate();

  return (
    <div
      style={{ position: 'absolute', top: 8, right: 8, zIndex: 1001, width: 288 }}
      className="bg-white rounded-lg shadow-xl border border-ink-200 p-4 space-y-3"
      data-testid="entity-panel"
    >
      {/* Cabecera */}
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="font-semibold text-ink-900 text-sm leading-tight truncate">
            {resumen.nombre}
          </div>
          <div className="text-[11px] text-ink-500 mt-0.5">
            {resumen.tipo} · {resumen.estado}
            {resumen.provincia && ` · ${resumen.provincia}`}
          </div>
        </div>
        <button
          onClick={onClose}
          className="p-1 rounded hover:bg-ink-100 text-ink-400 shrink-0"
        >
          <X size={14} />
        </button>
      </div>

      {/* Badges */}
      {resumen.badges?.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {resumen.badges.map((b) => <Badge key={b} id={b} />)}
        </div>
      )}

      {/* Contadores */}
      <div className="grid grid-cols-3 gap-1 text-center border-t border-ink-100 pt-2">
        {[
          { label: 'Visitas',    val: resumen.num_visitas },
          { label: 'Tareas',     val: resumen.num_tareas },
          { label: 'Contratos',  val: resumen.num_contratos_activos },
        ].map(({ label, val }) => (
          <div key={label}>
            <div className="text-base font-bold text-ink-900">{val ?? 0}</div>
            <div className="text-[10px] text-ink-400">{label}</div>
          </div>
        ))}
      </div>

      {/* Última visita */}
      {resumen.ultima_visita && (
        <div className="flex items-start gap-1.5 text-xs pt-1 border-t border-ink-100">
          <Clock size={12} className="text-ink-400 mt-0.5 shrink-0" />
          <div>
            <span className="text-ink-500">Última visita: </span>
            <span className="text-ink-700">
              {resumen.ultima_visita.fecha
                ? new Date(resumen.ultima_visita.fecha).toLocaleDateString('es-ES')
                : '—'}
            </span>
            {resumen.ultima_visita.tipo && (
              <span className="text-ink-400"> · {resumen.ultima_visita.tipo}</span>
            )}
          </div>
        </div>
      )}

      {/* Próxima tarea */}
      {resumen.proxima_tarea && (
        <div className="flex items-start gap-1.5 text-xs border-t border-ink-100 pt-1">
          <AlertCircle size={12} className="text-amber-500 mt-0.5 shrink-0" />
          <div className="min-w-0">
            <div className="text-ink-700 truncate">{resumen.proxima_tarea.titulo}</div>
            {resumen.proxima_tarea.fecha_limite && (
              <div className="text-ink-400">
                {new Date(resumen.proxima_tarea.fecha_limite).toLocaleDateString('es-ES')}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Acciones */}
      <div className="flex flex-wrap gap-1.5 pt-1 border-t border-ink-100">
        <Button
          size="sm" variant="outline" className="text-xs h-7 px-2"
          onClick={() => navigate(`/clientes/${resumen.id}`)}
        >
          <Eye size={11} className="mr-1" /> Ver ficha
        </Button>
        {resumen.telefono && (
          <Button
            size="sm" variant="outline" className="text-xs h-7 px-2"
            onClick={() => window.open(`tel:${resumen.telefono}`)}
          >
            <Phone size={11} className="mr-1" /> Llamar
          </Button>
        )}
        <Button
          size="sm" variant="outline" className="text-xs h-7 px-2"
          onClick={() => toast.info('Próximamente — registrar visita desde el mapa')}
        >
          <CalendarPlus size={11} className="mr-1" /> Visita
        </Button>
        <Button
          size="sm" variant="outline" className="text-xs h-7 px-2"
          onClick={() => navigate('/tareas')}
        >
          <ClipboardList size={11} className="mr-1" /> Tarea
        </Button>
      </div>
    </div>
  );
}
